import { useMemo, useRef, useState } from "react";
import { useSnackbar } from "notistack";
import {
  useAddIcpSamples,
  useDeleteIcpSample,
  useDeleteIcpSamples,
  useIcpSamples,
  useUpdateIcpSample,
} from "../hooks/useIcpSamples";
import { fileToText, formatTime } from "../lib/format";
import type { Contact, IcpSampleContact, SampleLabel } from "../lib/types";
import { ContactSearchList } from "./ContactSearchList";
import { StatusLine } from "./StatusLine";

export function IcpSampleManager({ icpName }: { icpName: string }) {
  const { enqueueSnackbar } = useSnackbar();
  const { data: samples, isLoading } = useIcpSamples(icpName);
  const add = useAddIcpSamples(icpName);
  const del = useDeleteIcpSample(icpName);
  const delMany = useDeleteIcpSamples(icpName);
  const update = useUpdateIcpSample(icpName);

  const positives = (samples ?? []).filter((sample) => sample.label === "positive");
  const negatives = (samples ?? []).filter((sample) => sample.label === "negative");

  // Apollo ids already in the sample pool — picker greys these out.
  const storedApolloIds = useMemo(
    () =>
      new Set(
        (samples ?? [])
          .map((sample) => sample.processed_contact_id)
          .filter((id): id is string => Boolean(id)),
      ),
    [samples],
  );

  const [error, setError] = useState<string | null>(null);

  const handleCsv = async (label: SampleLabel, file: File | undefined) => {
    if (!file) return;
    setError(null);
    try {
      const text = await fileToText(file);
      await add.mutateAsync({
        icp_name: icpName,
        csv_text: text,
        csv_label: label,
      });
      enqueueSnackbar(`Imported ${file.name} (${label}).`, { variant: "success" });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const handlePickContact = async (
    label: SampleLabel,
    contact: Contact,
    excludeReason?: string,
  ) => {
    setError(null);
    const trimmed = excludeReason?.trim();
    try {
      await add.mutateAsync({
        icp_name: icpName,
        processed_contact_ids: [contact.id],
        processed_label: label,
        processed_contact_notes:
          label === "negative" && trimmed ? { [contact.id]: trimmed } : undefined,
      });
      enqueueSnackbar(
        `Added ${contact.company || contact.domain} (${label}).`,
        { variant: "success" },
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const handleUpdateNotes = async (
    sample: IcpSampleContact,
    notes: string,
  ) => {
    const trimmed = notes.trim();
    if ((sample.notes ?? "") === trimmed) return;
    try {
      await update.mutateAsync({
        sample_id: sample.id,
        notes: trimmed ? trimmed : null,
      });
      enqueueSnackbar(
        trimmed ? `Updated reason for ${sample.domain}.` : `Cleared reason for ${sample.domain}.`,
        { variant: "success" },
      );
    } catch (err) {
      enqueueSnackbar(
        `Could not save: ${err instanceof Error ? err.message : err}`,
        { variant: "error" },
      );
    }
  };

  const handleDelete = async (sample: IcpSampleContact) => {
    if (
      !window.confirm(
        `Remove ${sample.domain} (${sample.label}) from this ICP's sample set?`,
      )
    )
      return;
    try {
      await del.mutateAsync({ sample_id: sample.id });
      enqueueSnackbar(`Removed ${sample.domain}.`, { variant: "success" });
    } catch (err) {
      enqueueSnackbar(
        `Could not remove: ${err instanceof Error ? err.message : err}`,
        { variant: "error" },
      );
    }
  };

  const handleBulkDelete = async (toRemove: IcpSampleContact[]) => {
    if (toRemove.length === 0) return;
    const noun = toRemove.length === 1 ? "sample" : "samples";
    if (
      !window.confirm(
        `Remove ${toRemove.length} ${noun} from this ICP's sample set?`,
      )
    )
      return;
    try {
      await delMany.mutateAsync({ sample_ids: toRemove.map((s) => s.id) });
      enqueueSnackbar(`Removed ${toRemove.length} ${noun}.`, {
        variant: "success",
      });
    } catch (err) {
      enqueueSnackbar(
        `Could not remove: ${err instanceof Error ? err.message : err}`,
        { variant: "error" },
      );
    }
  };

  return (
    <div className="field">
      <label>Sample contacts</label>
      <details className="field-hint">
        <summary>What's this?</summary>
        <p>
          Positive and negative example companies that the improve-prompt
          loop scores against. Persisted on the ICP so you can re-run
          authoring or validation any time — after a manual prompt edit, a
          model change, or just to spot-check the live scorer — without
          re-uploading.
        </p>
        <p>
          Two ways to add: <strong>Import CSV</strong> (Apollo / Lemlist
          export — full enrichment kept) or <strong>Add from contacts</strong>{" "}
          (pull a row that already lives in <code>processed_contacts</code>,
          which carries its Apollo enrichment as-is).
        </p>
        <p>
          Adding a sample with the same domain as an existing one (same
          label) updates the existing row.
        </p>
      </details>

      {isLoading && <StatusLine variant="running" message="Loading samples…" />}
      {error && <StatusLine variant="error" message={error} />}

      <div className="field-row" style={{ alignItems: "flex-start" }}>
        <SampleColumn
          label="positive"
          samples={positives}
          onCsv={(file) => handleCsv("positive", file)}
          onPickContact={(contact) => handlePickContact("positive", contact)}
          onDelete={handleDelete}
          onBulkDelete={handleBulkDelete}
          storedApolloIds={storedApolloIds}
          busy={add.isPending || del.isPending || delMany.isPending}
        />
        <SampleColumn
          label="negative"
          samples={negatives}
          onCsv={(file) => handleCsv("negative", file)}
          onPickContact={(contact, reason) =>
            handlePickContact("negative", contact, reason)
          }
          onDelete={handleDelete}
          onBulkDelete={handleBulkDelete}
          onUpdateNotes={handleUpdateNotes}
          storedApolloIds={storedApolloIds}
          busy={add.isPending || del.isPending || delMany.isPending || update.isPending}
        />
      </div>
    </div>
  );
}

function SampleRow({
  sample,
  selected,
  onToggleSelect,
  onDelete,
  onUpdateNotes,
  busy,
}: {
  sample: IcpSampleContact;
  selected: boolean;
  onToggleSelect: (sample: IcpSampleContact) => void;
  onDelete: (sample: IcpSampleContact) => void | Promise<void>;
  onUpdateNotes?: (sample: IcpSampleContact, notes: string) => void | Promise<void>;
  busy: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(sample.notes ?? "");
  const canEdit = Boolean(onUpdateNotes);

  // Reset draft when the row's notes change underneath us (e.g. after save).
  const lastNotesRef = useRef<string | null | undefined>(sample.notes);
  if (lastNotesRef.current !== sample.notes) {
    lastNotesRef.current = sample.notes;
    if (!editing) setDraft(sample.notes ?? "");
  }

  const commit = async () => {
    if (!onUpdateNotes) return;
    await onUpdateNotes(sample, draft);
    setEditing(false);
  };

  const cancel = () => {
    setDraft(sample.notes ?? "");
    setEditing(false);
  };

  return (
    <li
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        gap: 8,
        padding: "6px 8px",
        marginBottom: 4,
        background: "var(--card)",
        border: "1px solid var(--border)",
        borderRadius: 4,
        fontSize: 13,
      }}
    >
      <input
        type="checkbox"
        checked={selected}
        onChange={() => onToggleSelect(sample)}
        disabled={busy}
        aria-label={`Select ${sample.domain}`}
        style={{ marginTop: 3 }}
      />
      <div style={{ minWidth: 0, flex: 1 }}>
        <div
          style={{
            fontWeight: 600,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {sample.name || sample.domain}
        </div>
        <div className="card-desc" style={{ fontSize: 11 }}>
          <code>{sample.domain}</code> · {sample.source} ·{" "}
          {formatTime(sample.created_at)}
        </div>
        {canEdit && editing ? (
          <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
            <input
              type="text"
              value={draft}
              autoFocus
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") void commit();
                if (event.key === "Escape") cancel();
              }}
              placeholder="Why is this a bad fit?"
              style={{ flex: 1, fontSize: 12 }}
              disabled={busy}
            />
            <button type="button" onClick={() => void commit()} disabled={busy}>
              Save
            </button>
            <button
              type="button"
              className="button-link"
              onClick={cancel}
              disabled={busy}
            >
              Cancel
            </button>
          </div>
        ) : (
          <div
            className="card-desc"
            style={{ fontSize: 11, fontStyle: "italic", marginTop: 2 }}
          >
            {sample.notes ? (
              <>
                Why excluded: {sample.notes}
                {canEdit && (
                  <>
                    {" "}
                    <button
                      type="button"
                      className="button-link"
                      onClick={() => setEditing(true)}
                      disabled={busy}
                    >
                      Edit
                    </button>
                  </>
                )}
              </>
            ) : (
              canEdit && (
                <button
                  type="button"
                  className="button-link"
                  onClick={() => setEditing(true)}
                  disabled={busy}
                >
                  Add reason
                </button>
              )
            )}
          </div>
        )}
      </div>
      <button
        type="button"
        className="button-link"
        onClick={() => onDelete(sample)}
        disabled={busy}
        style={{ color: "var(--error)" }}
        aria-label={`Remove ${sample.domain}`}
      >
        ✕
      </button>
    </li>
  );
}

function SampleColumn({
  label,
  samples,
  onCsv,
  onPickContact,
  onDelete,
  onBulkDelete,
  onUpdateNotes,
  storedApolloIds,
  busy,
}: {
  label: SampleLabel;
  samples: IcpSampleContact[];
  onCsv: (file: File | undefined) => void | Promise<void>;
  onPickContact: (contact: Contact, reason?: string) => void | Promise<void>;
  onDelete: (sample: IcpSampleContact) => void | Promise<void>;
  onBulkDelete: (samples: IcpSampleContact[]) => void | Promise<void>;
  onUpdateNotes?: (sample: IcpSampleContact, notes: string) => void | Promise<void>;
  storedApolloIds: Set<string>;
  busy: boolean;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  // Drop selected ids that no longer exist (e.g. after a delete refreshes the
  // list) so the count and the bulk action stay accurate.
  const idsKey = samples.map((sample) => sample.id).join(",");
  const lastIdsRef = useRef(idsKey);
  if (lastIdsRef.current !== idsKey) {
    lastIdsRef.current = idsKey;
    setSelectedIds((prev) => {
      const live = new Set(
        [...prev].filter((id) => samples.some((sample) => sample.id === id)),
      );
      return live.size === prev.size ? prev : live;
    });
  }

  const toggleSelect = (sample: IcpSampleContact) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(sample.id)) next.delete(sample.id);
      else next.add(sample.id);
      return next;
    });

  const allSelected =
    samples.length > 0 && samples.every((sample) => selectedIds.has(sample.id));
  const toggleAll = () =>
    setSelectedIds(
      allSelected ? new Set() : new Set(samples.map((sample) => sample.id)),
    );

  const selectedSamples = samples.filter((sample) => selectedIds.has(sample.id));

  const headerColor = label === "positive" ? "var(--success)" : "var(--error)";

  return (
    <div className="field" style={{ flex: 1 }}>
      <label style={{ color: headerColor }}>
        {label === "positive" ? "Positive" : "Negative"} examples ({samples.length})
      </label>

      {samples.length === 0 ? (
        <p className="card-desc">No {label} samples yet.</p>
      ) : (
        <>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginBottom: 6,
              fontSize: 12,
            }}
          >
            <label
              style={{ display: "flex", alignItems: "center", gap: 6, margin: 0 }}
            >
              <input
                type="checkbox"
                checked={allSelected}
                onChange={toggleAll}
                disabled={busy}
                aria-label={`Select all ${label} samples`}
              />
              Select all
            </label>
            {selectedSamples.length > 0 && (
              <button
                type="button"
                className="button-link"
                onClick={() => onBulkDelete(selectedSamples)}
                disabled={busy}
                style={{ color: "var(--error)" }}
              >
                Remove selected ({selectedSamples.length})
              </button>
            )}
          </div>
          <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
            {samples.map((sample) => (
              <SampleRow
                key={sample.id}
                sample={sample}
                selected={selectedIds.has(sample.id)}
                onToggleSelect={toggleSelect}
                onDelete={onDelete}
                onUpdateNotes={onUpdateNotes}
                busy={busy}
              />
            ))}
          </ul>
        </>
      )}

      <div style={{ marginTop: 8, display: "flex", gap: 8, flexWrap: "wrap" }}>
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={busy}
        >
          Import CSV
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,text/csv"
          style={{ display: "none" }}
          onChange={(event) => {
            void onCsv(event.target.files?.[0]);
            event.target.value = "";
          }}
        />
        <button
          type="button"
          onClick={() => setPickerOpen((value) => !value)}
          disabled={busy}
        >
          {pickerOpen ? "Close picker" : "Add from contacts"}
        </button>
      </div>

      {pickerOpen && (
        <ContactPicker
          onPick={onPickContact}
          storedApolloIds={storedApolloIds}
          busy={busy}
          collectReason={label === "negative"}
        />
      )}
    </div>
  );
}

function ContactPicker({
  onPick,
  storedApolloIds,
  busy,
  collectReason,
}: {
  onPick: (contact: Contact, reason?: string) => void | Promise<void>;
  storedApolloIds: Set<string>;
  busy: boolean;
  collectReason: boolean;
}) {
  const [reasons, setReasons] = useState<Record<string, string>>({});

  return (
    <div style={{ marginTop: 12 }}>
      <ContactSearchList
        itemOpacity={(contact) => (storedApolloIds.has(contact.id) ? 0.6 : 1)}
        itemAction={(contact) => {
          const alreadyAdded = storedApolloIds.has(contact.id);
          const reason = reasons[contact.id] ?? "";
          return (
            <button
              type="button"
              onClick={() => onPick(contact, reason || undefined)}
              disabled={busy || alreadyAdded}
            >
              {alreadyAdded ? "Added" : "Add"}
            </button>
          );
        }}
        itemExtra={(contact) => {
          const alreadyAdded = storedApolloIds.has(contact.id);
          if (!collectReason || alreadyAdded) return null;
          const reason = reasons[contact.id] ?? "";
          return (
            <input
              type="text"
              value={reason}
              onChange={(event) =>
                setReasons((prev) => ({
                  ...prev,
                  [contact.id]: event.target.value,
                }))
              }
              placeholder="Why is this a bad fit? (optional, shown to the LLM)"
              style={{ width: "100%", fontSize: 12 }}
            />
          );
        }}
      />
    </div>
  );
}
